import type { PaymentScheduleEntry } from "./types";

function parseMoney(value: string): number | undefined {
  const normalized = value.replace(/^\$/, "").replace(/,/g, "").trim();
  const amount = Number(normalized);
  return Number.isFinite(amount) ? amount : undefined;
}

export function extractInvoiceMetadata(text: string): {
  invoiceNumber?: string;
  invoiceIssueDate?: string;
  subtotal?: number;
  invoiceTotal?: number;
  paymentSchedule: PaymentScheduleEntry[];
  warnings: string[];
} {
  const warnings: string[] = [];
  const lines = text.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);

  let invoiceNumber: string | undefined;
  const invoiceIdx = lines.findIndex((line) => /^invoice\s+#$/i.test(line));
  if (invoiceIdx >= 0 && lines[invoiceIdx + 1]) {
    invoiceNumber = lines[invoiceIdx + 1];
  } else {
    const inline = lines.find((line) => /^invoice\s*#\s*[\w-]+$/i.test(line));
    invoiceNumber = inline?.replace(/^invoice\s*#\s*/i, "").trim();
  }

  let invoiceIssueDate: string | undefined;
  const issuedIdx = lines.findIndex((line) => /^date\s+issued$/i.test(line));
  if (issuedIdx >= 0 && lines[issuedIdx + 1]) invoiceIssueDate = lines[issuedIdx + 1];

  const subtotalLine = lines.find((line) => /^subtotal\b/i.test(line));
  const subtotal = subtotalLine ? parseMoney(subtotalLine.replace(/^subtotal\s*/i, "")) : undefined;

  const totalLine = lines.find((line) => /^total\s*\(usd\)/i.test(line) || /^total\b/i.test(line));
  const invoiceTotal = totalLine ? parseMoney(totalLine.replace(/^total\s*(\(usd\))?\s*/i, "")) : undefined;

  const paymentSchedule: PaymentScheduleEntry[] = [];
  const scheduleIdx = lines.findIndex((line) => line.toLowerCase() === "payment schedule");
  if (scheduleIdx >= 0) {
    for (let index = scheduleIdx + 1; index < lines.length; index += 1) {
      const line = lines[index]!;
      if (/^--\s*\d+\s+of\s+\d+\s+--$/i.test(line)) continue;
      if (/^amount\s+due date/i.test(line.toLowerCase())) continue;
      if (!/\$\d/.test(line)) continue;
      const amountMatch = line.match(/\$[\d,]+(?:\.\d{2})?/);
      const statusMatch = line.match(/\b(deposited|paid|due|pending|overdue)\b/i);
      const paymentNumberMatch = line.match(/#\d[\w-]*/);
      const dateMatches = [...line.matchAll(/\b(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\s+\d{1,2},\s+\d{4}\b/gi)].map((match) => match[0]);
      paymentSchedule.push({
        amount: amountMatch ? parseMoney(amountMatch[0]) : undefined,
        dueDate: dateMatches[0],
        paymentDate: dateMatches[1],
        paymentNumber: paymentNumberMatch?.[0],
        status: statusMatch?.[1],
        rawLine: line,
      });
    }
  } else {
    warnings.push("Payment schedule section not detected.");
  }

  if (!invoiceNumber) warnings.push("Invoice number not detected.");
  if (!invoiceIssueDate) warnings.push("Invoice issue date not detected.");

  return { invoiceNumber, invoiceIssueDate, subtotal, invoiceTotal, paymentSchedule, warnings };
}

export function extractInvoiceLines(text: string): string[] {
  const lower = text.toLowerCase();
  const serviceIdx = lower.indexOf("service info");
  if (serviceIdx === -1) return [];

  const bodyStart = text.indexOf("\n", serviceIdx);
  const start = bodyStart === -1 ? serviceIdx + "service info".length : bodyStart + 1;
  const tail = text.slice(start);
  const subtotalMatch = tail.match(/subtotal\s*\$/i) ?? tail.match(/\bsubtotal\b/i);
  const end = subtotalMatch?.index ?? -1;
  if (end === -1) return [];

  return tail
    .slice(0, end)
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
}
